/* eslint-disable array-callback-return */
import React, {
  useCallback, useEffect, useState, useRef,
} from 'react';
import {
  StyleSheet, Text, ScrollView, View,
} from 'react-native';
import Moment from 'moment';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import { RootTabScreenProps } from '../../types';
import CompareArrowsIcon from '../../components/CompareArrowsIcon';
import SelectUtility, { Utility } from '../../components/SelectUtility';
import BarWithLineGraphWeb from '../../graphs/BarWithLineGraphWeb';
import CompareLineGraphWeb from '../../graphs/CompareLineGraphWeb';
import GetUtilitiesMetricByDate from '../../requests/Sensors/GetUtilitiesMetricByDate';
import TopSection, { screenMaxWidth, TopSectionProps } from '../../components/TopSection';
import BackgroundImage from '../../components/BackgroundImage';
import getClientDetails from '../../requests/HappyScore/GetClientDetails';
import { DateRange, DateSelectionRange, updateDateRange } from '../../redux/dateRangeSlice';
import { updateDateComparisonRange } from '../../redux/dateComparisonRangeSlice';
import ScreenEmptyState from '../../components/ScreenEmptyState';
import { HappyScoreCategory, Metric } from '../../constants/Types';
import isMobile from '../../components/IsMobile';
import MobileHeader from '../InternalEnvironment/components/MobileHeader';
import MobileOverview from './components/MobileOverview';
import MetricSelectionRow from '../InternalEnvironment/components/MetricSelectionRow';
import { ChartDataPoint } from '../../graphs/axisUtils';
import TargetBar from '../../components/Home/TargetBar';
import Style from '../../constants/Style';
import FixedDateComponent from '../../components/FixedDateComponent';
import TabButton from '../../components/TabButton';

export default function EnergyScreen({ route }: RootTabScreenProps<'Energy'>) {
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const electricRef = useRef<View>(null);
  const gasRef = useRef<View>(null);
  const co2Ref = useRef<View>(null);
  const waterRef = useRef<View>(null);
  const wasteRef = useRef<View>(null);
  const scrollViewRef = useRef(null);
  const compareRange: DateRange = useSelector((state: { dateComparisonRange: DateRange }) => (state
    .dateComparisonRange));
  const dispatch = useDispatch();
  const setRange = (dates: DateRange) => dispatch(updateDateRange(dates));
  const setCompareRange = (dates: DateRange) => dispatch(updateDateComparisonRange(dates));
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);

  const onConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setRange(
      { start, end, selectedRange },
    ),
    [setRange],
  );

  const onCompareConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setCompareRange(
      { start, end, selectedRange },
    ),
    [setCompareRange],
  );
  const [electricityTarget, setElectricityTarget] = useState(105);
  const [waterTarget, setWaterTarget] = useState(115);
  const [gasTarget, setGasTarget] = useState(280);
  const [co2Target, setCo2Target] = useState(40);

  useEffect(() => {
    getClientDetails().then((response) => {
      (response?.clientDetails?.building_daily_targets ?? []).forEach((item) => {
        if (item.name === 'electricity') {
          setElectricityTarget(item.target);
        } else if (item.name === 'water') {
          setWaterTarget(item.target);
        } else if (item.name === 'gas') {
          setGasTarget(item.target);
        } else if (item.name === 'co2') {
          setCo2Target(item.target);
        }
      });
    });
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const graph = (route.params as any)?.graph;
      if (electricRef.current && graph === 'electricity') {
        electricRef.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current.scrollTo({ x: 0, y, animated: true });
          },
        );
      } else if (gasRef.current && graph === 'gas') {
        gasRef.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current.scrollTo({ x: 0, y, animated: true });
          },
        );
      } else if (co2Ref.current && graph === 'co2') {
        co2Ref.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current.scrollTo({ x: 0, y, animated: true });
          },
        );
      } else if (wasteRef.current && graph === 'waste') {
        wasteRef.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current.scrollTo({ x: 0, y, animated: true });
          },
        );
      } else if (waterRef.current && graph === 'water') {
        waterRef.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current.scrollTo({ x: 0, y, animated: true });
          },
        );
      }
    }, 500);
  }, [route]);

  if (isMobile) {
    const [electricityGraphData, setElectricityGraphData] = useState<ChartDataPoint[] | null>(null);
    const [waterGraphData, setWaterGraphData] = useState<ChartDataPoint[] | null>(null);
    const [gasGraphData, setGasGraphData] = useState<ChartDataPoint[] | null>(null);
    const [emissionsGraphData, setEmissionsGraphData] = useState<ChartDataPoint[] | null>(null);
    const [wasteGraphData] = useState<ChartDataPoint[] | null>(null);
    const [electricityGraphComparisonData,
      setElectricityGraphComparisonData] = useState<ChartDataPoint[] | null>(null);
    const [waterGraphComparisonData,
      setWaterGraphComparisonData] = useState<ChartDataPoint[] | null>(null);
    const [gasGraphComparisonData,
      setGasGraphComparisonData] = useState<ChartDataPoint[] | null>(null);
    const [emissionsGraphComparisonData,
      setEmissionsGraphComparisonData] = useState<ChartDataPoint[] | null>(null);
    const [wasteGraphComparisonData] = useState<ChartDataPoint[] | null>(null);
    const [selectedMetricIndex, setSelectedMetricIndex] = useState<number | null>(null);
    const [electricityTotal, setElectricityTotal] = useState<number>(0);
    const [waterTotal, setWaterTotal] = useState<number>(0);
    const [gasTotal, setGasTotal] = useState<number>(0);
    const [emissionsTotal, setEmissionsTotal] = useState<number>(0);
    const [wasteTotal] = useState<number>(0);

    const transformData = (data) => data
      .map(({ value, timestamp }: { value: number, timestamp: string }, index: number) => ({
        value, index, date: timestamp,
      }));

    const getTotal = (data) => data.reduce((total: number, value) => total + value.value, 0);

    useEffect(() => {
      // @ts-ignore
      GetUtilitiesMetricByDate({ start: Moment().startOf('day'), end: Moment().endOf('day') })
        .then((res) => {
          setElectricityTotal(getTotal(transformData(res[0])));
          setGasTotal(getTotal(transformData(res[1])));
          setWaterTotal(getTotal(transformData(res[2])));
          setEmissionsTotal(getTotal(transformData(res[3])));
        });
    }, []);

    useEffect(() => {
      GetUtilitiesMetricByDate(range).then((res) => {
        setElectricityGraphData(transformData(res[0]));
        setGasGraphData(transformData(res[1]));
        setWaterGraphData(transformData(res[2]));
        setEmissionsGraphData(transformData(res[3]
          .map((point) => ({ ...point, value: point.value / 1000000 }))));
      });
    }, [range]);

    useEffect(() => {
      GetUtilitiesMetricByDate(compareRange).then((res) => {
        setElectricityGraphComparisonData(transformData(res[0]));
        setGasGraphComparisonData(transformData(res[1]));
        setWaterGraphComparisonData(transformData(res[2]));
        setEmissionsGraphComparisonData(transformData(res[3]
          .map((point) => ({ ...point, value: point.value / 1000000 }))));
      });
    }, [compareRange]);

    const items = [
      {
        title: 'Electricity',
        unit: 'kWh',
        target: electricityTarget,
        total: electricityTotal,
        data: electricityGraphData,
        comparisonData: electricityGraphComparisonData,
      },
      {
        title: 'Water',
        unit: 'litres',
        target: waterTarget,
        total: waterTotal,
        data: waterGraphData,
        comparisonData: waterGraphComparisonData,
      },
      {
        title: 'Gas',
        unit: 'm3',
        target: gasTarget,
        total: gasTotal,
        data: gasGraphData,
        comparisonData: gasGraphComparisonData,
      },
      {
        title: 'CO2',
        unit: 'm3',
        target: co2Target,
        total: emissionsTotal / 1000000,
        data: emissionsGraphData,
        comparisonData: emissionsGraphComparisonData,
      },
      {
        title: 'Waste',
        unit: 'kg',
        target: 500,
        total: wasteTotal,
        data: wasteGraphData,
        comparisonData: wasteGraphComparisonData,
      },
    ];

    return <MobileHeader
      screen={HappyScoreCategory.ENERGY}
      graphTitle={items[selectedMetricIndex]?.title}
      graphData={items[selectedMetricIndex]?.data}
      graphComparisonData={items[selectedMetricIndex]?.comparisonData}
      happyScoreMetric='group3EnergyAndWaste'
      overview={<MobileOverview {...{ items, setSelectedMetricIndex }} />}
      showContent={() => setSelectedMetricIndex(null)}
      historyFooter={<MetricSelectionRow
        titles={items.map((item) => item.title)}
        selectedTitle={items[selectedMetricIndex]?.title}
        select={(index: number) => setSelectedMetricIndex(index)}
      />}
      buildingSelectionOnly
    />;
  }

  function pad(n) {
    return n < 10 ? `0${n}` : n;
  }

  const toDate = (dateString) => {
    const dateobj = new Date(dateString);
    const hourLabel = `${pad(dateobj.getDate())}.${pad(dateobj.getMonth() + 1)}`;
    return hourLabel;
  };

  const [electricityTotal, setElectricityTotal] = useState(0);
  const [gasTotal, setGasTotal] = useState(0);
  const [waterTotal, setWaterTotal] = useState(0);
  const [emissionsTotal, setEmissionsTotal] = useState(0);

  const [electricityCompareTotal, setElectricityCompareTotal] = useState(0);
  const [gasCompareTotal, setGasCompareTotal] = useState(0);
  const [waterCompareTotal, setWaterCompareTotal] = useState(0);
  const [emissionsCompareTotal, setEmissionsCompareTotal] = useState(0);

  const [pointsCount, setPointsCount] = useState(1);
  const [comparePointsCount, setComparePointsCount] = useState(0);
  const [client, setClient] = useState('hereworks');

  const showTargets = false;

  const electricityData = [];
  const gasData = [];
  const waterData = [];
  const emissionsData = [];

  const electricityCompareData = [];
  const gasCompareData = [];
  const waterCompareData = [];
  const emissionsCompareData = [];

  useEffect(() => {
    AsyncStorage.getItem('clientName').then(setClient);
  }, []);

  const indexButtonData: Utility[] = [
    { title: 'Occupancy', metric: Metric.OCCUPANCY },
    { title: 'Electricity', metric: Metric.ELECTRICITY },
    { title: 'Water', metric: Metric.WATER },
    { title: 'Gas', metric: Metric.GAS },
  ];

  // Reading Data when the time frame is changed
  useEffect(() => {
    GetUtilitiesMetricByDate(
      range,
      null,
    ).then((data) => {
      if (data?.length > 0 && data[0]) {
        // prettier-ignore
        // ELECTRICITY
        data[0]?.map((e) => {
          if (!e.value) return;
          const date = toDate(e.timestamp);
          const point = { day: '', electricity: 0 };
          point.day = date;
          point.electricity = e.value
            ? Math.floor(e.value * 10) / 10 : 0;
          electricityData.push(point);

          const emissionsPoint = {
            day: date,
            emissions: (point.electricity * 179) / 1000,
          };

          emissionsData.push(emissionsPoint);
        });
        // GAS
        data[1]?.map((e) => {
          const date = toDate(e.timestamp);
          const point = {
            day: date,
            gas: e.value ?? 0,
          };
          gasData.push(point);
        });
        // WATER
        data[2]?.map((e) => {
          const date = toDate(e.timestamp);
          const point = {
            day: date,
            water: e.value ?? 0,
          };
          waterData.push(point);
        });
        let electricitySum = 0;
        electricityData.forEach((point) => {
          electricitySum += point.electricity;
        });
        setElectricityTotal(Math.floor(electricitySum * 10) / 10);
        let gasSum = 0;
        const startDate = Moment(range.start);
        const endDate = Moment(range.end);
        setPointsCount(endDate.diff(startDate, 'days') + 1);
        gasData.forEach((point) => {
          gasSum += point.gas;
        });
        setGasTotal(gasSum);
        let waterSum = 0;
        waterData.forEach((point) => {
          waterSum += point.water;
        });
        setWaterTotal(waterSum);
        let emissionsSum = 0;
        emissionsData.forEach((point) => {
          emissionsSum += point.emissions;
        });
        setEmissionsTotal(Math.round(100 * emissionsSum) / 100);
      }
    });
  }, [range.start, client]);

  // Reading Data when the time frame is changed
  useEffect(() => {
    GetUtilitiesMetricByDate(
      compareRange,
      null,
    ).then((data) => {
      if (data?.length > 0 && data[0] !== undefined) {
        // prettier-ignore
        // ELECTRICITY
        data[0]?.map((e) => {
          const date = toDate(e.timestamp);
          const point = { day: '', electricity: 0 };
          point.day = date;
          point.electricity = e.value
            ? Math.floor(
              e.value * 10,
            ) / 10
            : 0;
          electricityCompareData.push(point);

          const emissionsPoint = {
            day: date,
            emissions: (point.electricity * 179) / 1000,
          };

          emissionsCompareData.push(emissionsPoint);
        });
        // GAS
        data[1]?.map((e) => {
          const date = toDate(e.timestamp);
          const point = {
            day: date,
            gas: e.value ?? 0,
          };
          gasCompareData.push(point);
        });
        // WATER
        data[2]?.map((e) => {
          const date = toDate(e.timestamp);
          const point = {
            day: date,
            water: e.value ?? 0,
          };
          waterCompareData.push(point);
        });
        let electricitySum = 0;
        const startDate = Moment(compareRange.start);
        const endDate = Moment(compareRange.end);
        setComparePointsCount(endDate.diff(startDate, 'days') + 1);
        electricityCompareData.forEach((point) => {
          electricitySum += point.electricity;
        });
        setElectricityCompareTotal(Math.floor(electricitySum * 10) / 10);
        let gasSum = 0;
        gasCompareData.forEach((point) => {
          gasSum += point.gas;
        });
        setGasCompareTotal(gasSum);
        let waterSum = 0;
        waterCompareData.forEach((point) => {
          waterSum += point.water;
        });
        setWaterCompareTotal(waterSum);
        let emissionsSum = 0;
        emissionsCompareData.forEach((point) => {
          emissionsSum += point.emissions;
        });
        setEmissionsCompareTotal(Math.floor(emissionsSum));
      }
    });
  }, [compareRange.start, client]);

  const [previousMetric, setPreviousMetric] = useState<Utility>(indexButtonData[0]);
  const [currentMetric, setCurrentMetric] = useState<Utility>(indexButtonData[1]);
  const [utilityGraphVisible, setUtilityGraphVisible] = useState<boolean>(true);
  const [waterGraphVisible, setWaterGraphVisible] = useState<boolean>(true);
  const [elecGraphVisible, setElecGraphVisible] = useState<boolean>(true);
  const [gasGraphVisible, setGasGraphVisible] = useState<boolean>(true);
  const [emissionsGraphVisible, setEmissionsGraphVisible] = useState<boolean>(true);
  const [wasteGraphVisible, setWasteGraphVisible] = useState<boolean>(true);

  const compareDatesView = () => (
    <View
      style={{
        marginLeft: 24,
        flexDirection: 'row',
        backgroundColor: '#fff',
      }}
    >
      <Text
        style={{
          color: Colors.light.hereworksBlack600,
          fontSize: 12,
          fontFamily: 'Roboto_500Medium',
        }}
      >
        {Moment(range.start).format('D MMM YYYY')} {' - '}{' '}
        {Moment(range.end ?? range.start).format('D MMM YYYY')}
      </Text>
      {compareRange.start && (
        <>
          <CompareArrowsIcon style={{ marginHorizontal: 10 }} />
          <Text
            style={{
              color: Colors.light.hereworksYellow500,
              fontSize: 12,
              fontFamily: 'Roboto_500Medium',
            }}
          >
            {Moment(compareRange.start).format('D MMM YYYY')} {' - '}{' '}
            {Moment(compareRange.end ?? compareRange.start).format('D MMM YYYY')}
          </Text>
        </>
      )}
    </View>
  );

  const renderInsights = () => (
    <View style={styles.insights}>
      <View style={{ backgroundColor: '#fff', flex: 1 }}>
        <View style={styles.insightsTile}>
          <Text style={styles.insightsHeader}>Insight 1</Text>
          <Text style={styles.insightDesc}>Data not available</Text>
        </View>
        <View style={styles.insightsTile}>
          <Text style={styles.insightsHeader}>Insight 2</Text>
          <Text style={styles.insightDesc}>Data not available</Text>
        </View>
      </View>
      <View style={{ backgroundColor: '#fff', flex: 1 }}>
        <View style={styles.insightsTile}>
          <Text style={styles.insightsHeader}>Insight 3</Text>
          <Text style={styles.insightDesc}>Data not available</Text>
        </View>
        <View style={styles.insightsTile}>
          <Text style={styles.insightsHeader}>Insight 4</Text>
          <Text style={styles.insightDesc}>Data not available</Text>
        </View>
      </View>
    </View>
  );

  const Exceed = ({ exceeds }) => <View style={[Style.row, exceeds ? styles.exceed : styles.meet]}>
    <Text style={[styles.widgetPercent, exceeds ? { color: Colors.light.hereworksRed700 } : {}]}>
      {exceeds ? 'Exceed' : 'Meet'} targets</Text>
  </View>;

  const renderTotalWidget = (
    title,
    total,
    compareTotal,
    unit,
    target,
    compareTarget,
    descA,
    descB,
  ) => (
    <View style={styles.graphCard}>
      <View style={[styles.cardHeader, styles.totalGraphCardHeader]}>
        <Text style={styles.cardHeaderTitle}>{title}</Text>
        {compareRange.end === null ? <Exceed exceeds={total > target} /> : null}
      </View>
      {compareDatesView()}
      <View style={{ flexDirection: 'row' }}>
        <View style={{ flex: 1, marginHorizontal: 24 }}>
          <TargetBar current={Math.floor(total)} target={Math.floor(target)} unit={unit} />
          {compareRange.end !== null ? <TargetBar
            current={Math.floor(compareTotal)}
            target={Math.floor(compareTarget)} unit={unit} compare /> : null}
        </View>
        <View
          style={{
            width: 518,
            justifyContent: 'flex-end',
            marginBottom: 20,
            marginRight: 20,
            zIndex: -2,
          }}
        >
          <Text style={styles.totalDesc} selectable>{descA}</Text>
          <Text style={styles.totalDesc} selectable>{descB}</Text>
        </View>
      </View>
    </View>
  );

  const renderTargets = () => (
    <View
      style={{
        flexDirection: 'row',
        backgroundColor: 'transparent',
        borderColor: Colors.light.hereworksWhite500,
        borderTopWidth: 1.5,
        borderBottomWidth: 1.5,
        paddingVertical: 12,
        marginHorizontal: 24,
        marginBottom: 12,
      }}
    >
      <View
        style={{
          backgroundColor: 'transparent',
          flex: 1,
          borderColor: Colors.light.hereworksWhite500,
          borderRightWidth: 1.5,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            marginBottom: 8,
            backgroundColor: 'transparent',
            alignItems: 'center',
          }}
        >
          <View style={[styles.circleDot, { backgroundColor: Colors.light.hereworksGreen300 }]} />
          <Text style={styles.targetTitles}>Ideal</Text>
          <Text style={[styles.targetTitles, { flex: 1, textAlign: 'right', marginRight: 32 }]}>
            {'< 2400 kWh'}
          </Text>
        </View>
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: 'transparent',
            alignItems: 'center',
          }}
        >
          <View style={[styles.circleDot, { backgroundColor: Colors.light.hereworksBlue500 }]} />
          <Text style={styles.targetTitles}>Good</Text>
          <Text style={[styles.targetTitles, { flex: 1, textAlign: 'right', marginRight: 32 }]}>
            {'2400-2500 kWh'}
          </Text>
        </View>
      </View>
      <View style={{ backgroundColor: 'transparent', flex: 1, marginLeft: 32 }}>
        <View
          style={{
            flexDirection: 'row',
            marginBottom: 8,
            backgroundColor: 'transparent',
            alignItems: 'center',
          }}
        >
          <View style={[styles.circleDot, { backgroundColor: Colors.light.hereworksYellow800 }]} />
          <Text style={styles.targetTitles}>Tolerable</Text>
          <Text style={[styles.targetTitles, { flex: 1, textAlign: 'right' }]}>
            {'2500-2700 kWh'}
          </Text>
        </View>
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: 'transparent',
            alignItems: 'center',
          }}
        >
          <View style={[styles.circleDot, { backgroundColor: Colors.light.hereworksRed500 }]} />
          <Text style={styles.targetTitles}>Bad</Text>
          <Text style={[styles.targetTitles, { flex: 1, textAlign: 'right' }]}>
            {'2700-3000 kWh'}
          </Text>
        </View>
      </View>
    </View>
  );

  const topSectionProps: TopSectionProps = {
    screen: HappyScoreCategory.ENERGY,
  };

  return !notEmpty ? <ScreenEmptyState large />
    : <View style={{ flex: 1 }}>
      <ScrollView style={{ alignSelf: 'stretch', zIndex: -1 }} ref={scrollViewRef}>
        <View style={styles.container}>
          <BackgroundImage />
          <TopSection {...topSectionProps} />
          <View style={[styles.content, { maxWidth: screenMaxWidth }]}>
            <View style={styles.graphCard}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderTitle}>{'Utility vs occupancy'}</Text>
                <View style={Style.row}>
                  <TabButton
                    title='GRAPH'
                    select={() => setUtilityGraphVisible(true)}
                    selected={utilityGraphVisible}
                  />
                  <TabButton
                    title='INSIGHTS'
                    select={() => setUtilityGraphVisible(false)}
                    selected={!utilityGraphVisible}
                  />
                </View>
              </View>
              {compareDatesView()}
              {utilityGraphVisible && (
                <>
                  <Text
                    style={{
                      fontFamily: 'Poppins_400Regular',
                      fontSize: 22,
                      color: Colors.light.hereworksPrimaryGrey,
                      marginLeft: 24,
                      marginTop: 18,
                      marginBottom: 4,
                    }}
                  >
                    Overall utility
                  </Text>
                  <Text
                    style={{
                      fontFamily: 'Roboto_500Medium',
                      fontSize: 12,
                      color: Colors.light.hereworksBlack400,
                      marginLeft: 24,
                      marginTop: 4,
                      marginBottom: 4,
                    }}
                  >
                    You can choose only two parameters at once
                  </Text>
                  <View
                    style={{ backgroundColor: 'transparent', marginVertical: 12, marginLeft: 20 }}
                  >
                    <SelectUtility
                      data={indexButtonData}
                      compareToggled={compareRange.end !== null}
                      {...{
                        currentMetric,
                        setCurrentMetric,
                        previousMetric,
                        setPreviousMetric,
                      }}
                    />
                  </View>
                  <BarWithLineGraphWeb
                    dates={range}
                    compareDates={compareRange}
                    compareToggled={compareRange.end !== null}
                    title={currentMetric.title}
                    prevTitle={previousMetric.title}
                    metric={currentMetric.metric}
                    prevMetric={previousMetric.metric}
                  />
                  {showTargets && renderTargets()}
                </>
              )}
              {!utilityGraphVisible && renderInsights()}
            </View>
            <Text style={styles.sectionHeading}>Water</Text>
            {renderTotalWidget(
              'Water consumption',
              waterTotal,
              waterCompareTotal,
              'litres',
              pointsCount * waterTarget,
              comparePointsCount * waterTarget,
              'Water Consumption is measured by a water meter as the utility enters the building.\nWater consumption is generally closely related to the occupancy of the building so keep an eye on the correlation between water usage and the number of people in the building to spot anomalies.',
              'Report leaking taps, toilets and showers to the maintenance department. Do not leave any equipment that uses water running when not in use. Put on dishwashers with a full load and do not overfill kettles. Only use hot water when necessary to save energy.',
            )}
            <View style={styles.graphCard} ref={waterRef}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderTitle}>{'Water'}</Text>
                <View style={Style.row}>
                  <TabButton
                    title='GRAPH'
                    select={() => setWaterGraphVisible(true)}
                    selected={waterGraphVisible}
                  />
                  <TabButton
                    title='INSIGHTS'
                    select={() => setWaterGraphVisible(false)}
                    selected={!waterGraphVisible}
                  />
                </View>
              </View>
              {compareDatesView()}
              {waterGraphVisible && (
                <>
                  <CompareLineGraphWeb
                    dates={range}
                    compareDates={compareRange}
                    metricName={Metric.WATER}
                    title="Water"
                    thingId={null}
                    compareToggled={compareRange.end !== null}
                    unit='litres'
                  />
                </>
              )}
              {!waterGraphVisible && renderInsights()}
            </View>
            {renderTotalWidget(
              'Electricity overall usage',
              electricityTotal,
              electricityCompareTotal,
              'kWh',
              pointsCount * electricityTarget,
              comparePointsCount * electricityTarget,
              'Electricity is measured by smart meters. Meters can be placed on the main incoming supply to the building or area. Depending on how the electricity is distributed in the building, meters can also be placed per floor or per service (Lighting, General Services HVAC etc.)',
              'Switching off equipment when not in use is the key to saving on Electricity Usage. Air Conditioning generally uses the most electricity in offices. Appropriate Lighting Controls and LED fittings are best practice. Use the eco setting on all appliances. Ensure Audio Visual Equipment (Displays & Projectors) are switched off when not in use. For example, The power-saving mode on a photocopier can save up to 92% of its typical energy use.',
            )}
            <View
              style={styles.graphCard}
              ref={electricRef}
            >
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderTitle}>{'Electricity'}</Text>
                <View style={Style.row}>
                  <TabButton
                    title='GRAPH'
                    select={() => setElecGraphVisible(true)}
                    selected={elecGraphVisible}
                  />
                  <TabButton
                    title='INSIGHTS'
                    select={() => setElecGraphVisible(false)}
                    selected={!elecGraphVisible}
                  />
                </View>
              </View>
              {compareDatesView()}
              {elecGraphVisible && (
                <>
                  <CompareLineGraphWeb
                    dates={range}
                    compareDates={compareRange}
                    metricName={Metric.ELECTRICITY}
                    title="Electricity"
                    thingId={null}
                    compareToggled={compareRange.end !== null}
                    unit="kW h"
                  />
                </>
              )}
              {!elecGraphVisible && renderInsights()}
            </View>
            {renderTotalWidget(
              'Gas overall usage',
              gasTotal,
              gasCompareTotal,
              'm3',
              pointsCount * gasTarget,
              comparePointsCount * gasTarget,
              'Gas usage is measured by Gas meters as the utility enters the building.',
              'Keep windows and doors closed when the heating is on. Report areas that are too warm or too cold to the maintenance team. Report any defective windows and doors. Do not block radiators and heaters.',
            )}
            <View
              style={styles.graphCard}
              ref={gasRef}
            >
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderTitle}>{'Gas'}</Text>
                <View style={Style.row}>
                  <TabButton
                    title='GRAPH'
                    select={() => setGasGraphVisible(true)}
                    selected={gasGraphVisible}
                  />
                  <TabButton
                    title='INSIGHTS'
                    select={() => setGasGraphVisible(false)}
                    selected={!gasGraphVisible}
                  />
                </View>
              </View>
              {compareDatesView()}
              {gasGraphVisible && (
                <CompareLineGraphWeb
                  dates={range}
                  compareDates={compareRange}
                  metricName={Metric.GAS}
                  title="Gas"
                  compareToggled={compareRange.end !== null}
                  unit='m3'
                />
              )}
              {!gasGraphVisible && renderInsights()}
            </View>
            {renderTotalWidget(
              'CO2 emissions overall usage',
              emissionsTotal,
              emissionsCompareTotal,
              'kg',
              Math.round(pointsCount * co2Target * 10) / 10,
              Math.round(comparePointsCount * co2Target * 10) / 10,
              'CO2 (Carbon Dioxide) emissions are calculated by combining all of the CO2 producing sources in the building (Gas, Electricity, Oil etc) and multiplying by the number of kg’s of CO2 produced per unit. For electricity the average Fuel Mix is used (provide by your electricity supplier) to calculate how many kg’s of CO2 are produced per kw/hr.',
              'Reducing your use of Electricity, Gas and Oil will reduce your CO2 emissions. CO2 emissions can also be offset by using renewable energy sources such as solar & wind.',
            )}
            <View
              style={styles.graphCard}
              ref={co2Ref}
            >
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderTitle}>{'CO2 emissions'}</Text>
                <View style={Style.row}>
                  <TabButton
                    title='GRAPH'
                    select={() => setEmissionsGraphVisible(true)}
                    selected={emissionsGraphVisible}
                  />
                  <TabButton
                    title='INSIGHTS'
                    select={() => setEmissionsGraphVisible(false)}
                    selected={!emissionsGraphVisible}
                  />
                </View>
              </View>
              {compareDatesView()}
              {emissionsGraphVisible && (
                <CompareLineGraphWeb
                  dates={range}
                  compareDates={compareRange}
                  metricName="emissions"
                  title="CO2 emissions"
                  compareToggled={compareRange.end !== null}
                  unit="kg"
                />
              )}
              {!emissionsGraphVisible && renderInsights()}
            </View>
            {renderTotalWidget(
              'Waste overall usage',
              0,
              0,
              'kg',
              Math.round(pointsCount * 500 * 10) / 10,
              Math.round(comparePointsCount * 500 * 10) / 10,
              'Waste Usage can be measured using bin sensors and/or external data sources such as waste collection data.',
              'Waste is generally measured in Kg’s and can be segregated by waste type (Landfill/Recycling/Compostable etc).',
            )}
            <View
              style={styles.graphCard}
              ref={wasteRef}
            >
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderTitle}>{'Waste'}</Text>
                <View style={Style.row}>
                  <TabButton
                    title='GRAPH'
                    select={() => setWasteGraphVisible(true)}
                    selected={wasteGraphVisible}
                  />
                  <TabButton
                    title='INSIGHTS'
                    select={() => setWasteGraphVisible(false)}
                    selected={!wasteGraphVisible}
                  />
                </View>
              </View>
              {compareDatesView()}
              {wasteGraphVisible && (
                <CompareLineGraphWeb
                  dates={range}
                  compareDates={compareRange}
                  metricName={'processed_waste_volume_accumulator'}
                  title="Waste"
                  compareToggled={compareRange.end !== null}
                  unit="kg"
                />
              )}
              {!wasteGraphVisible && renderInsights()}
            </View>
          </View>
        </View>
      </ScrollView>
      <FixedDateComponent onConfirm={onConfirm} onCompareConfirm={onCompareConfirm} liveEnabled />
    </View>;
}

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    alignItems: 'stretch',
    backgroundColor: Colors.light.hereworksBackground,
  },
  content: {
    backgroundColor: 'transparent',
    paddingHorizontal: 24,
  },
  graphCard: {
    marginVertical: 12,
    marginEnd: 16,
    borderRadius: 24,
    backgroundColor: 'white',
  },
  sectionHeading: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 22,
    color: Colors.light.hereworksPrimaryGrey,
  },
  targetTitles: {
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  circleDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  insightsTile: {
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 16,
    margin: 6,
  },
  insightsHeader: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
    margin: 16,
  },
  insightDesc: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    marginHorizontal: 16,
    marginBottom: 50,
  },
  totalDesc: {
    backgroundColor: Colors.light.hereworksWhite100,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue200,
    borderRadius: 16,
    padding: 16,
    margin: 4,
  },
  cardHeader: {
    ...Style.row,
    paddingHorizontal: 24,
    marginTop: 24,
    marginBottom: 8,
  },
  totalGraphCardHeader: {
    justifyContent: 'flex-start',
  },
  cardHeaderTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 18,
    color: Colors.light.hereworksPrimaryGrey,
  },
  insights: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    flex: 2,
    margin: 18,
  },
  widgetPercent: {
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksGreen500,
    fontSize: 14,
  },
  exceed: {
    borderColor: Colors.light.hereworksRed700,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 16,
    backgroundColor: Colors.light.hereworksRed50,
  },
  meet: {
    borderColor: Colors.light.hereworksGreen,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 16,
    backgroundColor: Colors.light.hereworksGreen50,
  },
});
